import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NotifBadge } from "@/context/NotificationContext";
import { CATEGORIES } from "@/lib/constants";
import { JeekotaLogo, VerificationTick, TrustScore } from "@/components/shared";
import { FollowUsLinks } from "@/components/shared/SocialIcon";
import { SearchOverlay } from "@/components/layout/SearchOverlay";
import { StartCampaignStrip } from "@/components/shared/StartCampaignStrip";
import { Home, MessageCircle, TrendingUp, Heart, Bell, User, Search, Menu, LogOut, Shield, FileText, DollarSign, Mail, LayoutGrid, BarChart3, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

// Main Layout with Navigation
const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const navItems = [
    { icon: Home, label: "Home", path: "/home", color: null },
    { icon: MessageCircle, label: "Discussions", path: "/discussions", color: "text-blue-500" },
    { icon: TrendingUp, label: "Validation Campaigns", path: "/crowdfunding", color: "text-amber-500" },
    { icon: Heart, label: "Donations", path: "/donations", color: "text-red-500" },
    { icon: LayoutGrid, label: "Categories", path: "#categories", color: null },
  ];

  return (
    <div className="min-h-screen bg-jeekota-bg">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-jeekota-border hidden lg:flex lg:flex-col overflow-hidden">
        <div className="p-6 flex-shrink-0">
          <Link to="/home" className="flex items-center gap-2">
            <JeekotaLogo size={40} />
            <span className="text-xl font-bold text-jeekota-primary font-display">jeekota</span>
          </Link>
        </div>
        
        <div className="flex-1 overflow-y-auto px-3 pb-3">
        <nav>
          {navItems.map((item) => (
            item.path === "#categories" ? (
              <button
                key="categories"
                data-testid="nav-categories"
                onClick={() => setShowCategories(true)}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl mb-0.5 transition-colors text-left text-jeekota-text hover:bg-jeekota-primary/10"
              >
                <item.icon size={18} />
                <span className="font-medium text-sm">{item.label}</span>
              </button>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl mb-0.5 transition-all duration-200 ${
                  location.pathname === item.path
                    ? 'bg-jeekota-primary text-white shadow-sm'
                    : 'text-jeekota-text hover:bg-jeekota-primary/10 hover:translate-x-0.5'
                }`}
              >
                <item.icon size={18} className={location.pathname === item.path ? '' : (item.color || '')} />
                <span className={item.color ? 'text-base italic' : 'text-sm font-medium'}>{item.label}</span>
              </Link>
            )
          ))}
        </nav>

        {/* Bottom section */}
        <div className="mt-3 pt-3 border-t border-jeekota-border">
          {user ? (
            <>
              <Link
                to="/profile"
                data-testid="nav-profile"
                className="flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-jeekota-primary/10 transition-colors"
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-jeekota-primary text-white text-xs">
                    {user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm flex items-center min-w-0">
                    <span className="truncate">{user?.name}</span>
                    <VerificationTick type={user?.verification_tick} />
                  </p>
                  <TrustScore score={user?.trust_score || 100} />
                </div>
              </Link>
              
              {/* Compact nav links */}
              <div className="grid grid-cols-2 gap-1 mt-2">
                <Link to="/notifications" data-testid="nav-notifications"
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-jeekota-text hover:bg-jeekota-primary/10 transition-colors relative">
                  <Bell size={15} />
                  <span className="text-xs font-medium">Alerts</span>
                  <NotifBadge />
                </Link>
                <Link to="/creator" data-testid="nav-creator"
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors">
                  <BarChart3 size={15} />
                  <span className="text-xs font-medium">Dashboard</span>
                </Link>
                {user?.is_admin && (
                  <Link to="/admin" data-testid="nav-admin"
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors">
                    <Shield size={15} />
                    <span className="text-xs font-medium">Admin</span>
                  </Link>
                )}
                <Link to="/legal" data-testid="nav-legal-desktop"
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-jeekota-text-secondary hover:bg-jeekota-primary/10 transition-colors">
                  <FileText size={15} />
                  <span className="text-xs font-medium">Legal</span>
                </Link>
                <Link to="/about" data-testid="nav-about-desktop"
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-jeekota-text-secondary hover:bg-jeekota-primary/10 transition-colors">
                  <FileText size={15} />
                  <span className="text-xs font-medium">About</span>
                </Link>
                <Link to="/fees" data-testid="nav-fees-desktop"
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-jeekota-text-secondary hover:bg-jeekota-primary/10 transition-colors">
                  <DollarSign size={15} />
                  <span className="text-xs font-medium">Fees</span>
                </Link>
                <Link to="/faq" data-testid="nav-faq-desktop"
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-jeekota-text-secondary hover:bg-jeekota-primary/10 transition-colors">
                  <HelpCircle size={15} />
                  <span className="text-xs font-medium">Why Fundraise</span>
                </Link>
                <Link to="/contact" data-testid="nav-contact-desktop"
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-jeekota-text-secondary hover:bg-jeekota-primary/10 transition-colors">
                  <Mail size={15} />
                  <span className="text-xs font-medium">Contact</span>
                </Link>
              </div>
              
              <FollowUsLinks />
              
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2 text-red-500 hover:text-red-600 hover:bg-red-50 mt-2 h-9"
                onClick={() => { logout(); navigate('/home'); }}
              >
                <LogOut size={16} />
                <span className="text-sm">Logout</span>
              </Button>
            </>
          ) : (
            <div className="space-y-2">
              <Link
                to="/legal"
                data-testid="nav-legal-desktop"
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl mb-1 text-jeekota-text-secondary hover:bg-jeekota-primary/10 transition-colors"
              >
                <FileText size={18} />
                <span className="font-medium text-sm">Legal</span>
              </Link>
              <Link
                to="/about"
                data-testid="nav-about-desktop-loggedout"
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl mb-1 text-jeekota-text-secondary hover:bg-jeekota-primary/10 transition-colors"
              >
                <FileText size={18} />
                <span className="font-medium text-sm">About Us</span>
              </Link>
              <Link
                to="/fees"
                data-testid="nav-fees-desktop-loggedout"
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl mb-1 text-jeekota-text-secondary hover:bg-jeekota-primary/10 transition-colors"
              >
                <DollarSign size={18} />
                <span className="font-medium text-sm">Fees</span>
              </Link>
              <Link
                to="/faq"
                data-testid="nav-faq-desktop-loggedout"
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl mb-1 text-jeekota-text-secondary hover:bg-jeekota-primary/10 transition-colors"
              >
                <HelpCircle size={18} />
                <span className="font-medium text-sm">Why Fundraise</span>
              </Link>
              <Link
                to="/contact"
                data-testid="nav-contact-desktop-loggedout"
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl mb-1 text-jeekota-text-secondary hover:bg-jeekota-primary/10 transition-colors"
              >
                <Mail size={18} />
                <span className="font-medium text-sm">Contact Us</span>
              </Link>
              <FollowUsLinks />
              <Button
                data-testid="sidebar-signin-btn"
                className="w-full bg-jeekota-primary hover:bg-jeekota-primary/90"
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
              <Button
                data-testid="sidebar-signup-btn"
                variant="outline"
                className="w-full border-jeekota-primary text-jeekota-primary hover:bg-jeekota-primary/10"
                onClick={() => navigate('/login', { state: { signup: true } })}
              >
                Create Account
              </Button>
            </div>
          )}
        </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 h-10 lg:h-12 bg-white border-b border-jeekota-border flex items-center justify-between px-3 lg:hidden z-50">
        <Link to="/home" className="flex items-center gap-1.5 flex-shrink-0">
          <JeekotaLogo size={26} />
          <span className="text-base font-bold text-jeekota-primary font-display">jeekota</span>
        </Link>
        
        <Link
          to="/vision"
          data-testid="vision-header-btn"
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors flex-shrink-0"
        >
          <span className="text-base leading-none">📌</span>
          <span className="text-xs font-semibold text-amber-800 whitespace-nowrap">Vision & Mission</span>
        </Link>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button 
            variant="ghost" 
            size="icon" 
            data-testid="header-search-btn"
            onClick={() => setShowSearch(true)}
          >
            <Search size={22} />
          </Button>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu size={24} />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72">
            <div className="py-4">
              {user ? (
                <div className="flex items-center gap-3 p-3 mb-4">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-jeekota-primary text-white">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-medium flex items-center min-w-0">
                      <span className="truncate">{user?.name}</span>
                      <VerificationTick type={user?.verification_tick} />
                    </p>
                    <TrustScore score={user?.trust_score || 100} />
                  </div>
                </div>
              ) : (
                <div className="p-3 mb-4">
                  <p className="font-bold text-jeekota-primary text-lg">jeekota</p>
                  <p className="text-sm text-jeekota-text-secondary">Browse the community</p>
                </div>
              )}
              
              <nav className="space-y-0.5">
                {navItems.map((item) => {
                  if (item.path === "#categories") return null;
                  if (item.path === "/home") return null;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => { setMobileMenuOpen(false); setShowCategories(false); }}
                      className={`flex items-center gap-3 px-4 py-2 rounded-xl transition-colors ${
                        location.pathname === item.path
                          ? 'bg-jeekota-primary text-white'
                          : 'text-jeekota-text hover:bg-jeekota-primary/10'
                      }`}
                    >
                      <item.icon size={18} />
                      <span className="font-medium text-sm">{item.label}</span>
                    </Link>
                  );
                })}
                
                <Link
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="nav-about-mobile"
                  className={`flex items-center gap-3 px-4 py-2 rounded-xl transition-colors ${
                    location.pathname === '/about'
                      ? 'bg-jeekota-primary text-white'
                      : 'text-jeekota-text hover:bg-jeekota-primary/10'
                  }`}
                >
                  <FileText size={18} />
                  <span className="font-medium text-sm">About Us</span>
                </Link>
                
                {user && (
                  <Link
                    to="/notifications"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 rounded-xl text-jeekota-text hover:bg-jeekota-primary/10 transition-colors relative"
                  >
                    <Bell size={18} />
                    <span className="font-medium text-sm">Notifications</span>
                    <NotifBadge />
                  </Link>
                )}
                
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="nav-contact-mobile"
                  className="flex items-center gap-3 px-4 py-2 rounded-xl text-jeekota-text hover:bg-jeekota-primary/10 transition-colors"
                >
                  <Mail size={18} />
                  <span className="font-medium text-sm">Contact Us</span>
                </Link>
                
                {user && (
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 rounded-xl text-jeekota-text hover:bg-jeekota-primary/10 transition-colors"
                  >
                    <User size={18} />
                    <span className="font-medium text-sm">Profile</span>
                  </Link>
                )}
                
                {user?.is_admin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 rounded-xl text-amber-600 hover:bg-amber-50 transition-colors"
                  >
                    <Shield size={18} />
                    <span className="font-medium text-sm">Admin Panel</span>
                  </Link>
                )}
                
                <Separator className="my-2" />
                
                <Link
                  to="/legal"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 rounded-xl text-jeekota-text hover:bg-jeekota-primary/10 transition-colors"
                  data-testid="nav-legal"
                >
                  <FileText size={18} />
                  <span className="font-medium text-sm">Legal & Guidelines</span>
                </Link>
                
                <Link
                  to="/fees"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 rounded-xl text-jeekota-text hover:bg-jeekota-primary/10 transition-colors"
                  data-testid="nav-fees-mobile"
                >
                  <DollarSign size={18} />
                  <span className="font-medium text-sm">Fees</span>
                </Link>
                
                <Link
                  to="/faq"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 rounded-xl text-jeekota-text hover:bg-jeekota-primary/10 transition-colors"
                  data-testid="nav-faq-mobile"
                >
                  <HelpCircle size={18} />
                  <span className="font-medium text-sm">Why Fundraise</span>
                </Link>
                
                {user && (
                  <Link
                    to="/creator"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 rounded-xl text-emerald-600 hover:bg-emerald-50 transition-colors"
                    data-testid="nav-creator-mobile"
                  >
                    <TrendingUp size={18} />
                    <span className="font-medium text-sm">Creator Dashboard</span>
                  </Link>
                )}

                <Separator className="my-2" />
                <FollowUsLinks compact />
                
                {user ? (
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                      navigate('/home');
                    }}
                  >
                    <LogOut size={20} />
                    <span>Logout</span>
                  </Button>
                ) : (
                  <div className="space-y-2 pt-2">
                    <Button
                      className="w-full bg-jeekota-primary hover:bg-jeekota-primary/90"
                      onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                    >
                      Sign In
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full border-jeekota-primary text-jeekota-primary hover:bg-jeekota-primary/10"
                      onClick={() => { setMobileMenuOpen(false); navigate('/login', { state: { signup: true } }); }}
                    >
                      Create Account
                    </Button>
                  </div>
                )}
              </nav>
            </div>
          </SheetContent>
        </Sheet>
        </div>
      </header>

      {/* Search Full-Page Overlay */}
      {showSearch && (
        <SearchOverlay 
          onClose={() => setShowSearch(false)} 
        />
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 h-11 bg-white border-t border-jeekota-border flex items-center justify-around lg:hidden z-50">
        {navItems.map((item) => (
          item.path === "#categories" ? (
            <button
              key="categories-bottom"
              onClick={() => setShowCategories(true)}
              data-testid="bottom-nav-categories"
              className={`flex flex-col items-center gap-0.5 p-1 ${
                showCategories ? 'text-jeekota-primary' : 'text-jeekota-text-secondary'
              }`}
            >
              <item.icon size={18} />
              <span className="text-[10px]">{item.label}</span>
            </button>
          ) : (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setShowCategories(false)}
              className={`flex flex-col items-center gap-0.5 p-1 transition-colors ${
                location.pathname === item.path
                  ? 'text-jeekota-primary'
                  : 'text-jeekota-text-secondary'
              }`}
            >
              <item.icon size={18} className={location.pathname === item.path ? '' : (item.color || '')} />
              <span className={`text-[10px] ${item.color ? 'text-[11px] italic' : ''}`}>{item.label}</span>
            </Link>
          )
        ))}
      </nav>

      {/* Categories Dialog - triggered by bottom nav and sidebar */}
      <Dialog open={showCategories} onOpenChange={setShowCategories}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Browse Categories</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto py-2">
            {CATEGORIES.map(cat => (
              <Link
                key={cat}
                to={`/home?category=${encodeURIComponent(cat)}`}
                onClick={() => setShowCategories(false)}
                data-testid={`cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-3 py-2.5 rounded-xl text-sm font-medium text-jeekota-text hover:bg-jeekota-primary/10 hover:text-jeekota-primary transition-colors"
              >
                {cat}
              </Link>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="lg:ml-64 lg:pt-0 pb-14 lg:pb-0">
        {/* Mobile spacer for fixed header */}
        <div className="h-10 lg:hidden" />
        {/* Desktop Header with Search */}
        <div className="hidden lg:flex items-center justify-between px-6 py-3 border-b border-jeekota-border bg-white sticky top-0 z-40">
          <div />
          <Link
            to="/vision"
            data-testid="vision-desktop-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
          >
            <span className="text-base leading-none">📌</span>
            <span className="text-sm font-semibold text-amber-800">Vision & Mission</span>
          </Link>
          <Button 
            variant="ghost" 
            size="icon" 
            data-testid="desktop-search-btn"
            onClick={() => setShowSearch(true)}
            className="text-jeekota-text-secondary hover:text-jeekota-text"
          >
            <Search size={20} />
          </Button>
        </div>
        <StartCampaignStrip />
        {/* Donations LIVE ticker */}
        <Link
          to="/donations"
          className="block w-full overflow-hidden bg-zinc-900"
          data-testid="donations-live-ticker"
        >
          <div className="inline-block whitespace-nowrap animate-marquee text-amber-300 text-[11px] font-semibold py-1">
            ❤️ Donations Crowdfunding is now LIVE! Donate with Paystack 💳 &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ❤️ Donations Crowdfunding is now LIVE! Donate with Paystack 💳 &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </div>
        </Link>
        {children}
      </main>

      {/* Floating Create Button - REMOVED, replaced by START A CAMPAIGN strip */}
    </div>
  );
};


export { MainLayout };
